import { IconGraphy, Table, Tooltip } from '@leapeasy/ui-kit';
import { Typography } from '@leapeasy/ui-kit';
import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { getDemoData } from 'utils/helpers';
import { USER_ROLE } from 'data/constants/common_constants';
import styled from 'styled-components';
import { ListExportScopeModal } from '../shared/ListExportScopeModal';
import { GlobalUXState } from 'components/GlobalUXState';
import {
  fitContentCompactColumnOptions,
  fitContentTextColumnOptions,
} from 'utils/fitContentTextColumnOptions';
import { USER_TABLE_DEFAULT_VISIBLE_KEYS, USER_TABLE_STORAGE_KEY } from './userTableConfig';

const LEAP_PURPLE = '#702572';
const DISABLED_ICON_COLOR = '#B8B0BC';

const dateColumnOptions = (overrides = {}) => ({
  width: 104,
  minWidth: 104,
  maxWidth: 104,
  shrink: false,
  ...overrides,
});

export const UserTable = forwardRef(
  (
    {
      title,
      role,
      setSelecteduser,
      users,
      filter,
      dropFilter,
      onChangeFilter,
      pagination,
      onChangePagination,
      sortOptions,
      onChangeSort,
      onClickUser,
      onClickEdit,
      onRequestExportData,
      onRequestFullExportData,
      exportFullDatasetTotal,
      dropFilterActiveCount,
      dropFilterActiveSummary,
      loading = false,
      showUserRole = false,
      enableRowBodyClick = false,
      defaultVisibleColumnKeys = USER_TABLE_DEFAULT_VISIBLE_KEYS,
      columnVisibilityStorageKey = USER_TABLE_STORAGE_KEY,
      emptyContent,
    },
    ref,
  ) => {
    const [tableData, setTableData] = useState([]);
    const [exportModalOpen, setExportModalOpen] = useState(false);
    const tableRef = useRef(null);
    useImperativeHandle(ref, () => ({
      exportCsv: (...args) => tableRef.current?.exportCsv?.(...args),
    }));
    const isDemo = useSelector((state) => state.getIn(['ui', 'demo']));

    const buildUserRow = useCallback(
      (user, index) => {
        const row = [
          isDemo ? getDemoData('username') : user.accepter.username,
          user.accepter.job_title,
          isDemo ? getDemoData('email') : user.accepter_email,
          user.property.length,
        ];
        if (showUserRole) {
          row.push(USER_ROLE[user.accepter_role] || user.accepter_role || '');
        }
        row.push(
          new Date(user.createdAt).toISOString().slice(0, 10),
          isDemo ? getDemoData('phone') : user.accepter.phone,
          index,
        );
        return row;
      },
      [isDemo, showUserRole],
    );

    useEffect(() => {
      if (!users) {
        setTableData([]);
        return;
      }
      setTableData(users.map((user, index) => buildUserRow(user, index)));
    }, [users, buildUserRow]);

    const goDetailPage = useCallback(
      (user) => {
        if (user) {
          onClickUser(user);
        }
      },
      [onClickUser],
    );

    const goEditPage = useCallback(
      (user) => {
        if (user) {
          onClickEdit(user);
        }
      },
      [onClickEdit],
    );

    const getUserFromRow = (row) => users?.[row?.[row.length - 1]];

    const renderRowBodyCell = (value, user) => (
      <CustomTypography
        align="left"
        variant="body2"
        style={enableRowBodyClick && onClickUser ? { cursor: 'pointer' } : undefined}
        onClick={enableRowBodyClick && onClickUser && user ? () => onClickUser(user) : undefined}
      >
        {value}
      </CustomTypography>
    );

    const columns = useMemo(() => {
      const userRoleColumn = showUserRole
        ? [
            {
              name: 'User Role',
              options: {
                ...fitContentTextColumnOptions({ minWidth: 100, maxWidth: 200 }),
                sort: true,
                customBodyRenderer: (value, row) => renderRowBodyCell(value, getUserFromRow(row)),
              },
              key: 'accepter_role',
            },
          ]
        : [];

      return [
        {
          name: 'Name',
          options: {
            minWidth: 140,
            flexGrow: 1,
            ellipsis: false,
            sort: true,
            customBodyRenderer: (value, row) => renderRowBodyCell(value, getUserFromRow(row)),
          },
          key: 'accepter.username',
        },
        {
          name: 'Job Title',
          options: {
            ...fitContentTextColumnOptions({ minWidth: 120, maxWidth: 240, flexGrow: 0 }),
            sort: true,
            customBodyRenderer: (value, row) => renderRowBodyCell(value, getUserFromRow(row)),
          },
          key: 'accepter.job_title',
        },
        {
          name: 'Email Address',
          options: {
            ...fitContentTextColumnOptions({ minWidth: 160, maxWidth: 320, flexGrow: 0 }),
            sort: true,
            customBodyRenderer: (value, row) => renderRowBodyCell(value, getUserFromRow(row)),
          },
          key: 'accepter_email',
        },
        {
          name: 'Property Count',
          options: {
            ...fitContentCompactColumnOptions({ minWidth: 96, maxWidth: 120, align: 'center' }),
            sort: true,
            customBodyRenderer: (value) => (
              <Typography align="center" variant="body1">
                {value.toLocaleString('en-US')}
              </Typography>
            ),
          },
          key: 'property.length',
        },
        ...userRoleColumn,
        {
          name: 'Invited Date',
          options: {
            ...dateColumnOptions(),
            customBodyRenderer: (value, row) => renderRowBodyCell(value, getUserFromRow(row)),
          },
          key: 'createdAt',
        },
        {
          name: 'Phone Number',
          options: {
            ...fitContentTextColumnOptions({ minWidth: 120, maxWidth: 180 }),
            sort: true,
            customBodyRenderer: (value, row) => renderRowBodyCell(value, getUserFromRow(row)),
          },
          key: 'accepter.phone',
        },
        {
          name: 'Actions',
          key: 'actions',
          options: {
            width: 112,
            minWidth: 112,
            maxWidth: 112,
            shrink: false,
            sort: false,
            align: 'center',
            excludeFromExport: true,
            stickyRight: true,
            customBodyRenderer: (rowIndex) => {
              const user = users?.[rowIndex];
              const canViewDetails = Boolean(user) && Boolean(onClickUser);
              const canManageProperties = Boolean(user?.id) && Boolean(onClickEdit);
              const canDelete = Boolean(user) && Boolean(setSelecteduser);

              return (
                <ActionsCell onClick={(e) => e.stopPropagation()}>
                  <ActionIconButton
                    icon="FileFolder.Description"
                    title="View user details"
                    disabled={!canViewDetails}
                    onClick={() => goDetailPage(user)}
                  />
                  <ActionIconButton
                    icon="HomePlaces.Apartment"
                    title="Manage user properties"
                    disabled={!canManageProperties}
                    onClick={() => goEditPage(user)}
                  />
                  <ActionIconButton
                    icon="General.Delete"
                    title="Delete user"
                    disabled={!canDelete}
                    onClick={() => {
                      if (canDelete) {
                        setSelecteduser(user);
                      }
                    }}
                  />
                </ActionsCell>
              );
            },
          },
        },
      ];
    }, [
      showUserRole,
      users,
      onClickUser,
      onClickEdit,
      setSelecteduser,
      goDetailPage,
      goEditPage,
      enableRowBodyClick,
    ]);

    return (
      <>
        <Table
          loading={loading}
          showEmptyState
          emptyContent={
            emptyContent ?? (
              <GlobalUXState
                variant={filter?.searchText ? 'noResults' : 'noData'}
                compact
                description={
                  filter?.searchText ? 'No users match your search.' : 'No users available yet.'
                }
              />
            )
          }
          ref={tableRef}
          onExportButtonClick={() => setExportModalOpen(true)}
          onRequestExportData={
            onRequestExportData
              ? async () => {
                  const rows = await onRequestExportData();
                  return (rows || []).map((user, index) => buildUserRow(user, index));
                }
              : undefined
          }
          onRequestFullExportData={
            onRequestFullExportData
              ? async () => {
                  const rows = await onRequestFullExportData();
                  return (rows || []).map((user, index) => buildUserRow(user, index));
                }
              : undefined
          }
          data={tableData}
          autoTooltipForDefaultCells
          defaultVisibleColumnKeys={defaultVisibleColumnKeys}
          columnVisibilityStorageKey={columnVisibilityStorageKey}
          columnWidthStorageKey={columnVisibilityStorageKey}
          filter={filter}
          dropFilter={dropFilter}
          dropFilterActiveCount={dropFilterActiveCount}
          dropFilterActiveSummary={dropFilterActiveSummary}
          onChangeFilter={onChangeFilter}
          onChangeRowsPerPage={(value) =>
            onChangePagination({
              ...pagination,
              rowsPerPage: value,
              pageNumber: 0,
            })
          }
          pagination={pagination}
          sortOptions={sortOptions}
          onChangeSort={onChangeSort}
          style={{ width: '100%' }}
          title={title}
          columns={columns}
        />
        <ListExportScopeModal
          open={exportModalOpen}
          onClose={() => setExportModalOpen(false)}
          tableRef={tableRef}
          title={title}
          currentPageRowCount={tableData.length}
          paginationTotalItems={pagination.totalItems}
          fullDatasetTotal={exportFullDatasetTotal}
          enableFiltered={Boolean(onRequestExportData)}
          enableFull={Boolean(onRequestFullExportData)}
          enableSelected={false}
        />
      </>
    );
  },
);

UserTable.displayName = 'UserTable';

const ActionsCell = styled.div`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
`;

const ActionIconButton = ({ icon, title, disabled, onClick }) => (
  <Tooltip title={title} placement="top" showCarot>
    <span>
      <ActionIcon
        type="button"
        disabled={disabled}
        aria-label={title}
        onClick={disabled ? undefined : onClick}
      >
        <IconGraphy icon={icon} style={{ color: disabled ? DISABLED_ICON_COLOR : LEAP_PURPLE }} />
      </ActionIcon>
    </span>
  </Tooltip>
);

const ActionIcon = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: none;
  background: transparent;
  line-height: 0;

  &:not(:disabled) {
    cursor: pointer;
  }

  &:disabled {
    cursor: default;
    pointer-events: none;
  }
`;

const CustomTypography = styled(Typography)`
  word-break: normal;
  overflow-wrap: break-word;
`;
